import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon } from './Icon'
import type { Quota, Status } from '../types'

export function Card({ children, className = '', title, action }: { children: ReactNode; className?: string; title?: ReactNode; action?: ReactNode }) {
  return <section className={`card ${className}`}>{title && <div className="card-head"><div>{title}</div>{action}</div>}{children}</section>
}

export function Badge({ status, children }: { status: Status; children?: ReactNode }) {
  const label = children || statusLabel(status)
  return <span className={`badge badge-${statusClass(status)}`}><span className="badge-dot" />{label}</span>
}

export function Progress({ value, warning = 80, hard = 95, showLabel = false }: { value: number; warning?: number; hard?: number; showLabel?: boolean }) {
  const normalized = clamp(value, 0, 100)
  const state = normalized >= hard ? 'danger' : normalized >= warning ? 'warning' : 'normal'
  return <div className={`progress-wrap progress-${state}`}><div className="progress-track"><div className="progress-fill" style={{ width: `${normalized}%` }} /></div>{showLabel && <span>{normalized.toFixed(1)}%</span>}</div>
}

export function Ring({ value, label, caption }: { value: number; label: string; caption?: string }) {
  const normalized = clamp(value, 0, 100)
  const style = { '--ring-value': `${normalized * 3.6}deg` } as CSSProperties
  return <div className="metric-ring-wrap"><div className="metric-ring" style={style}><div><strong>{normalized.toFixed(0)}%</strong><span>{label}</span></div></div>{caption && <small>{caption}</small>}</div>
}

export function Modal({ open, title, children, onClose, wide = false }: { open: boolean; title: string; children: ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = previous }
  }, [open, onClose])
  if (!open) return null
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}><div className="modal-head"><div><span className="eyebrow">TIANCLOUD</span><h2>{title}</h2></div><button className="icon-button" onClick={onClose} aria-label="关闭"><Icon name="x" /></button></div><div className="modal-body">{children}</div></div></div>
}

export function CopyButton({ text, compact = false }: { text: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1400)
  }
  return <button className={compact ? 'icon-button' : 'button button-secondary'} onClick={copy}>{copied ? <Icon name="check" /> : <Icon name="copy" />}{!compact && <span>{copied ? '已复制' : '复制'}</span>}</button>
}

export function Empty({ icon = 'cloud', title, description }: { icon?: string; title: string; description: string }) {
  return <div className="empty-state"><div className="empty-icon"><Icon name={icon} size={26} /></div><strong>{title}</strong><p>{description}</p></div>
}

export function quotaPercent(quota?: Quota): number {
  if (!quota || quota.limit <= 0) return 0
  return (quota.used / quota.limit) * 100
}

export function quotaRemaining(quota?: Quota): number {
  if (!quota || quota.limit <= 0) return Number.POSITIVE_INFINITY
  return Math.max(0, quota.limit - quota.used)
}

export function formatQuota(quota?: Quota): string {
  if (!quota) return '—'
  if (quota.limit <= 0) return quota.unit === 'unlimited' ? '不限额' : `${formatQuotaValue(quota.used, quota.unit)} 已使用`
  return `${formatQuotaValue(quota.used, quota.unit)} / ${formatQuotaValue(quota.limit, quota.unit)}`
}

export function formatRemaining(quota?: Quota): string {
  if (!quota) return '—'
  if (quota.limit <= 0) return '不限额'
  return formatQuotaValue(quotaRemaining(quota), quota.unit)
}

export function formatQuotaValue(value = 0, unit = ''): string {
  const normalized = unit.trim().toLowerCase()
  if (['byte', 'bytes', 'b'].includes(normalized)) return formatBytes(value)
  if (normalized === 'usd' || normalized === '$') return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value)
  if (normalized === 'gbp' || normalized === '£') return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(value)
  if (normalized === 'cny' || normalized === 'rmb' || normalized === '¥') return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY', maximumFractionDigits: 2 }).format(value)
  if (normalized === '%' || normalized === 'percent') return `${formatNumber(value)}%`
  if (normalized === 'unlimited') return '不限额'
  return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`
}

export function formatBytes(value = 0): string {
  if (!Number.isFinite(value) || value <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  const amount = value / 1024 ** index
  return `${amount >= 100 ? amount.toFixed(0) : amount >= 10 ? amount.toFixed(1) : amount.toFixed(2)} ${units[index]}`
}

export function formatNumber(value = 0): string {
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2, notation: Math.abs(value) >= 1_000_000 ? 'compact' : 'standard' }).format(value)
}

export function formatPercent(value = 0, digits = 1): string {
  if (value > 0 && value < 0.1) return '<0.1%'
  return `${value.toFixed(digits)}%`
}

export function formatDate(value?: string, includeTime = true): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('zh-CN', includeTime ? { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' } : { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

export function formatRelative(value?: string): string {
  if (!value) return '未知'
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000)
  const abs = Math.abs(seconds)
  const formatter = new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' })
  if (abs < 60) return formatter.format(seconds, 'second')
  if (abs < 3600) return formatter.format(Math.round(seconds / 60), 'minute')
  if (abs < 86400) return formatter.format(Math.round(seconds / 3600), 'hour')
  return formatter.format(Math.round(seconds / 86400), 'day')
}

export function formatDuration(seconds = 0): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days} 天 ${hours} 小时`
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `${hours} 小时 ${minutes} 分钟`
  return `${minutes} 分钟`
}

export function nodeName(nodeId: string, nodes: { id: string; name: string }[]): string {
  return nodes.find((node) => node.id === nodeId)?.name || nodeId
}

export function statusLabel(status: Status): string {
  const labels: Record<string, string> = {
    healthy: '可用', warning: '接近额度', quota_exhausted: '额度硬停', expired: '已到期', error: '同步异常', needs_config: '待配置', unknown: '待同步',
    online: '在线', offline: '离线', pending: '待确认', queued: '排队中', running: '执行中', succeeded: '成功', failed: '失败', active: '已生效', disabled: '已停用', ready: '就绪',
  }
  return labels[status] || status
}

export function statusClass(status: Status): string {
  if (['healthy', 'online', 'succeeded', 'active', 'ready'].includes(status)) return 'success'
  if (['warning', 'pending', 'queued', 'running', 'unknown'].includes(status)) return 'warning'
  if (['quota_exhausted', 'expired', 'error', 'offline', 'failed'].includes(status)) return 'danger'
  return 'neutral'
}

export function serviceLabel(service: string): string {
  const labels: Record<string, string> = {
    storage: '存储与分发', llm: 'AI 模型', tts: '文本转语音', stt: '语音转文本', search: '搜索 API', ocr: 'OCR / 文档识别', dns: 'DNS / 域名', custom: '自定义服务',
  }
  return labels[service] || service
}

export function areaLabel(area = 'global'): string {
  const labels: Record<string, string> = { global: '自动 / 全球', asia: '亚洲', europe: '欧洲', north_america: '北美洲', south_america: '南美洲', oceania: '大洋洲', africa: '非洲', middle_east: '中东' }
  return labels[area] || area
}

export function lifecycleLabel(lifecycle = 'long_term'): string {
  const labels: Record<string, string> = { long_term: '长期节点', temporary: '临时节点', dynamic: '动态节点' }
  return labels[lifecycle] || lifecycle
}

export function trustLabel(trust = 'trusted'): string {
  return trust === 'limited' ? '不太可信' : '可信'
}

export function managementLabel(mode = 'automatic'): string {
  return mode === 'manual' ? '手动管理' : '自动托管'
}

export function effectiveNodeRegion(location?: { source?: string; region?: string; detected_region?: string }): string {
  if (!location) return '自动识别'
  if (location.source === 'automatic' && location.detected_region) return location.detected_region
  return location.region && location.region !== 'auto' ? location.region : '自动识别'
}

export function providerIcon(kind: string): string {
  if (kind.includes('search')) return 'search'
  if (kind.includes('speech') || kind.includes('transcribe') || kind.includes('tts') || kind.includes('polly')) return 'activity'
  if (kind.includes('ollama') || kind.includes('antigravity') || kind.includes('openai') || kind.includes('vertex') || kind.includes('gemini') || kind.includes('bedrock')) return 'brain'
  if (kind.includes('cloudflare')) return 'globe'
  if (kind.includes('local')) return 'server'
  if (kind.includes('drive')) return 'cloud'
  return 'database'
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0))
}
